// src/pages/admin/TatlStoryCreate.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";

import AdminLayout from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { createAdminTatlStory, uploadBlogImage } from "../../lib/adminApi";
import { API_URL } from "../../lib/utils";

Quill.register("modules/imageResize", ImageResize);

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1200&q=80";

const uid = () =>
  `block_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const toJsonList = (text) => {
  const lines = String(text || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

  return JSON.stringify(lines);
};

const escapeHtml = (value = "") =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";

  const clean = url.trim();
  if (!clean) return "";

  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;

  return `${BACKEND_ORIGIN}/${clean}`;
};

const getYouTubeEmbed = (url) => {
  const clean = String(url || "").trim();
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/i,
    /youtu\.be\/([^?]+)/i,
    /youtube\.com\/shorts\/([^?]+)/i,
    /youtube\.com\/embed\/([^?]+)/i,
  ];

  for (const pattern of patterns) {
    const match = clean.match(pattern);
    if (match?.[1]) return `https://www.youtube.com/embed/${match[1]}`;
  }

  return "";
};

const buildEmbedHtml = (url, blockId = uid()) => {
  const clean = String(url || "").trim();
  if (!clean) return "";

  const youtube = getYouTubeEmbed(clean);

  if (youtube) {
    return `
      <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 overflow-hidden rounded-2xl border border-stone-200 bg-black shadow-sm">
        <div class="relative w-full pt-[56.25%]">
          <iframe src="${youtube}" class="absolute left-0 top-0 h-full w-full" frameborder="0" allowfullscreen loading="lazy"></iframe>
        </div>
      </div>
    `;
  }

  if (clean.includes("facebook.com")) {
    return `
      <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 overflow-hidden rounded-2xl border border-stone-200 bg-stone-50 p-4">
        <iframe src="https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(
          clean
        )}&show_text=true&width=700" class="mx-auto w-full min-h-[420px]" frameborder="0" scrolling="no" allowfullscreen loading="lazy"></iframe>
      </div>
    `;
  }

  if (clean.includes("instagram.com") || clean.includes("tiktok.com")) {
    return `
      <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        <p class="mb-3 text-sm font-semibold text-stone-700">Embedded social post</p>
        <a href="${escapeHtml(clean)}" target="_blank" rel="noopener noreferrer" class="break-words text-amber-700 underline">${escapeHtml(clean)}</a>
      </div>
    `;
  }

  return `
    <div data-post-block="embed" data-embed-url="${escapeHtml(clean)}" data-block-id="${blockId}" class="my-8 rounded-2xl border border-stone-200 bg-stone-50 p-5">
      <a href="${escapeHtml(clean)}" target="_blank" rel="noopener noreferrer" class="break-words text-amber-700 underline">${escapeHtml(clean)}</a>
    </div>
  `;
};

const widthToClass = (width) => {
  if (width === "33") return "md:w-1/3";
  if (width === "50") return "md:w-1/2";
  if (width === "75") return "md:w-3/4";
  return "w-full";
};

const galleryColsToClass = (cols) => {
  if (Number(cols) === 6) return "md:grid-cols-6";
  if (Number(cols) === 4) return "md:grid-cols-4";
  return "md:grid-cols-2";
};

const removeWidthClasses = (el) => {
  if (!el) return;
  ["w-full", "md:w-1/3", "md:w-1/2", "md:w-3/4"].forEach((c) =>
    el.classList.remove(c)
  );
};

const removeGalleryClasses = (el) => {
  if (!el) return;
  ["md:grid-cols-2", "md:grid-cols-4", "md:grid-cols-6"].forEach((c) =>
    el.classList.remove(c)
  );
};

const TatlStoryCreate = () => {
  const nav = useNavigate();

  const quillRef = useRef(null);
  const singleImageInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const selectedElementRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [title, setTitle] = useState("");
  const [childName, setChildName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [impactDate, setImpactDate] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [quote, setQuote] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const [highlights, setHighlights] = useState("");
  const [needs, setNeeds] = useState("");
  const [supportGiven, setSupportGiven] = useState("");
  const [outcomes, setOutcomes] = useState("");

  const [content, setContent] = useState("<p></p>");
  const [isPublished, setIsPublished] = useState(true);

  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [gallery, setGallery] = useState([]);

  const [showEmbedBox, setShowEmbedBox] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryCols, setGalleryCols] = useState(2);
  const [galleryImages, setGalleryImages] = useState([]);

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);

  const canSave = useMemo(() => {
    return (
      title.trim() &&
      childName.trim() &&
      location.trim() &&
      impactDate.trim() &&
      excerpt.trim() &&
      coverImage
    );
  }, [title, childName, location, impactDate, excerpt, coverImage]);

  const getEditor = () => quillRef.current?.getEditor();

  const syncEditorContent = () => {
    const html = getEditor()?.root?.innerHTML || "<p></p>";
    setContent(html);
  };

  const clearSelectionOutline = () => {
    const root = getEditor()?.root;
    root?.querySelectorAll("[data-selected-block='true']").forEach((el) => {
      el.removeAttribute("data-selected-block");
      el.classList.remove("ring-4", "ring-amber-500", "ring-offset-2");
    });
  };

  const readSelectedElement = (el) => {
    if (!el) return null;

    if (el.tagName === "A") {
      return {
        type: "link",
        href: el.getAttribute("href") || "",
        text: el.textContent || "",
        target: el.getAttribute("target") || "",
      };
    }

    const type = el.getAttribute("data-post-block");

    if (type === "image") {
      const img = el.querySelector("img");
      const caption = el.querySelector("figcaption");

      return {
        type: "image",
        src: img?.getAttribute("src") || "",
        alt: img?.getAttribute("alt") || "",
        caption: caption?.textContent || "",
        width: el.getAttribute("data-width") || "full",
      };
    }

    if (type === "gallery") {
      const figures = Array.from(el.querySelectorAll("figure"));

      return {
        type: "gallery",
        cols: Number(el.getAttribute("data-cols") || 2),
        items: figures.map((fig, index) => ({
          index,
          src: fig.querySelector("img")?.getAttribute("src") || "",
          caption: fig.querySelector("figcaption")?.textContent || "",
        })),
      };
    }

    if (type === "embed") {
      return {
        type: "embed",
        url: el.getAttribute("data-embed-url") || "",
      };
    }

    return null;
  };

  const selectEditorBlock = (element) => {
    if (!element) return;

    clearSelectionOutline();

    const block =
      element.closest("[data-post-block]") ||
      (element.tagName === "A" ? element : element.closest("a"));

    if (!block) {
      selectedElementRef.current = null;
      setSelectedBlock(null);
      return;
    }

    selectedElementRef.current = block;
    block.setAttribute("data-selected-block", "true");
    block.classList.add("ring-4", "ring-amber-500", "ring-offset-2");

    setSelectedBlock(readSelectedElement(block));
  };

  const refreshSelectedBlock = () => {
    setSelectedBlock(readSelectedElement(selectedElementRef.current));
  };

  const deleteSelectedBlock = () => {
    const el = selectedElementRef.current;
    if (!el) return;

    if (el.tagName === "A") {
      el.replaceWith(document.createTextNode(el.textContent || ""));
    } else {
      el.remove();
    }

    selectedElementRef.current = null;
    setSelectedBlock(null);
    setContextMenu(null);
    syncEditorContent();
  };

  const insertHtml = (html) => {
    const editor = getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength();

    editor.clipboard.dangerouslyPasteHTML(index, html);
    editor.setSelection(index + 1);

    setTimeout(syncEditorContent, 0);
  };

  const uploadFile = async (file) => {
    const data = await uploadBlogImage(file);
    if (!data?.image_url) throw new Error("Upload failed");
    return normalizeImageUrl(data.image_url);
  };

  const insertSingleImage = async (file) => {
    if (!file) return;

    setUploadingImage(true);

    try {
      const url = await uploadFile(file);
      const blockId = uid();

      insertHtml(`
        <figure data-post-block="image" data-block-id="${blockId}" data-width="full" class="my-8 w-full">
          <img src="${url}" alt="" class="w-full rounded-2xl border border-stone-200 object-cover shadow-sm" loading="lazy" />
          <figcaption class="mt-2 text-center text-sm italic text-stone-500"></figcaption>
        </figure>
      `);
    } catch (err) {
      alert(err?.message || "Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const openGalleryFiles = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;

    setUploadingImage(true);

    try {
      const uploaded = [];

      for (const file of selected) {
        const url = await uploadFile(file);
        uploaded.push({ url, caption: "" });
      }

      setGalleryImages(uploaded);
      setGalleryOpen(true);
    } catch (err) {
      alert(err?.message || "Gallery upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const insertGallery = () => {
    if (!galleryImages.length) return;

    const blockId = uid();
    const colClass = galleryColsToClass(galleryCols);

    const html = `
      <div data-post-block="gallery" data-blog-gallery="true" data-block-id="${blockId}" data-cols="${galleryCols}" class="my-10 grid grid-cols-1 ${colClass} gap-4">
        ${galleryImages
          .map(
            (img, index) => `
            <figure data-gallery-item="${index}" class="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
              <img src="${img.url}" alt="${escapeHtml(img.caption || "")}" class="h-56 w-full object-cover" loading="lazy" />
              <figcaption class="px-3 py-2 text-center text-sm italic text-stone-500">${escapeHtml(
                img.caption || ""
              )}</figcaption>
            </figure>
          `
          )
          .join("")}
      </div>
    `;

    insertHtml(html);
    setGalleryOpen(false);
    setGalleryImages([]);
  };

  const insertEmbed = () => {
    const html = buildEmbedHtml(embedUrl);
    if (!html) return;

    insertHtml(html);
    setEmbedUrl("");
    setShowEmbedBox(false);
  };

  const onCoverImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setCoverImage(file);

    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview(file ? URL.createObjectURL(file) : "");
  };

  const updateImageSetting = (key, value) => {
    const figure = selectedElementRef.current;
    if (!figure || selectedBlock?.type !== "image") return;

    const img = figure.querySelector("img");
    const caption = figure.querySelector("figcaption");

    if (key === "caption" && caption) caption.textContent = value;
    if (key === "alt" && img) img.setAttribute("alt", value);

    if (key === "width") {
      figure.setAttribute("data-width", value);
      removeWidthClasses(figure);
      figure.classList.add(...widthToClass(value).split(" "));
    }

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateGalleryLayout = (cols) => {
    const galleryBlock = selectedElementRef.current;
    if (!galleryBlock || selectedBlock?.type !== "gallery") return;

    galleryBlock.setAttribute("data-cols", String(cols));
    removeGalleryClasses(galleryBlock);
    galleryBlock.classList.add(...galleryColsToClass(cols).split(" "));

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateGalleryCaption = (index, caption) => {
    const galleryBlock = selectedElementRef.current;
    if (!galleryBlock || selectedBlock?.type !== "gallery") return;

    const fig = galleryBlock.querySelector(`[data-gallery-item="${index}"]`);
    const cap = fig?.querySelector("figcaption");
    const img = fig?.querySelector("img");

    if (cap) cap.textContent = caption;
    if (img) img.setAttribute("alt", caption);

    syncEditorContent();
    refreshSelectedBlock();
  };

  const deleteGalleryItem = (index) => {
    const galleryBlock = selectedElementRef.current;
    if (!galleryBlock || selectedBlock?.type !== "gallery") return;

    galleryBlock.querySelector(`[data-gallery-item="${index}"]`)?.remove();

    const remaining = Array.from(galleryBlock.querySelectorAll("figure"));
    remaining.forEach((fig, i) =>
      fig.setAttribute("data-gallery-item", String(i))
    );

    if (!remaining.length) {
      galleryBlock.remove();
      selectedElementRef.current = null;
      setSelectedBlock(null);
    }

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateLinkSetting = (key, value) => {
    const link = selectedElementRef.current;
    if (!link || selectedBlock?.type !== "link") return;

    if (key === "href") link.setAttribute("href", value);
    if (key === "text") link.textContent = value;

    if (key === "target") {
      if (value) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      } else {
        link.removeAttribute("target");
        link.removeAttribute("rel");
      }
    }

    syncEditorContent();
    refreshSelectedBlock();
  };

  const updateEmbedUrl = (url) => {
    const embed = selectedElementRef.current;
    if (!embed || selectedBlock?.type !== "embed") return;

    const blockId = embed.getAttribute("data-block-id") || uid();
    const wrapper = document.createElement("div");
    wrapper.innerHTML = buildEmbedHtml(url, blockId).trim();
    const newEmbed = wrapper.firstElementChild;

    if (!newEmbed) return;

    embed.replaceWith(newEmbed);
    selectedElementRef.current = newEmbed;
    syncEditorContent();
    refreshSelectedBlock();
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!selectedElementRef.current) return;

      const active = document.activeElement;
      const isTypingField =
        active?.tagName === "INPUT" ||
        active?.tagName === "TEXTAREA" ||
        active?.isContentEditable;

      if (isTypingField) return;

      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelectedBlock();
      }

      if (e.key === "Escape") {
        clearSelectionOutline();
        selectedElementRef.current = null;
        setSelectedBlock(null);
        setContextMenu(null);
      }
    };

    const closeMenu = () => setContextMenu(null);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("click", closeMenu);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("click", closeMenu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlock]);

  useEffect(() => {
    return () => {
      if (coverPreview) URL.revokeObjectURL(coverPreview);
    };
  }, [coverPreview]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, false] }],
          [{ font: [] }],
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["clean"],
        ],
        handlers: {
          image: () => singleImageInputRef.current?.click(),
        },
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "align",
    "list",
    "bullet",
    "indent",
    "blockquote",
    "code-block",
    "link",
    "image",
    "video",
    "width",
    "height",
    "style",
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!title.trim()) return alert("Title is required");
    if (!childName.trim()) return alert("Child name is required");
    if (!location.trim()) return alert("Location is required");
    if (!impactDate.trim()) return alert("Impact date is required (YYYY-MM-DD)");
    if (!excerpt.trim()) return alert("Excerpt is required");
    if (!coverImage) return alert("Cover image is required");

    setSaving(true);

    try {
      syncEditorContent();

      const fd = new FormData();

      fd.append("title", title.trim());
      fd.append("child_name", childName.trim());

      const ageVal = String(age || "").trim();
      if (ageVal) fd.append("age", ageVal);

      const genderVal = String(gender || "").trim();
      if (genderVal) fd.append("gender", genderVal);

      fd.append("location", location.trim());
      fd.append("impact_date", impactDate.trim());
      fd.append("excerpt", excerpt.trim());
      fd.append("content", (getEditor()?.root?.innerHTML || content || "<p></p>").trim() || "<p></p>");

      fd.append("highlights_json", toJsonList(highlights));
      fd.append("needs_json", toJsonList(needs));
      fd.append("support_given_json", toJsonList(supportGiven));
      fd.append("outcomes_json", toJsonList(outcomes));

      const quoteVal = String(quote || "").trim();
      if (quoteVal) fd.append("quote", quoteVal);

      const videoVal = String(videoUrl || "").trim();
      if (videoVal) fd.append("video_url", videoVal);

      fd.append("is_published", isPublished ? "true" : "false");
      fd.append("cover_image", coverImage);

      (gallery || []).forEach((f) => {
        if (f) fd.append("gallery_images", f);
      });

      await createAdminTatlStory(fd);
      nav("/admin/tatl");
    } catch (err) {
      alert(err?.message || "Failed to create story");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Create TATL1000 Story">
      <form onSubmit={onSubmit} className="space-y-6">
        <Card className="rounded-2xl border-stone-200">
          <CardContent className="space-y-5 p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="tatl-title">Story Title</Label>
                <Input
                  id="tatl-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Example: School Support Restored Hope"
                  required
                />
              </div>

              <div>
                <Label htmlFor="tatl-child-name">Child Name</Label>
                <Input
                  id="tatl-child-name"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  placeholder="Child name (public-facing)"
                  required
                />
              </div>

              <div>
                <Label htmlFor="tatl-impact-date">Impact Date</Label>
                <Input
                  id="tatl-impact-date"
                  type="date"
                  value={impactDate}
                  onChange={(e) => setImpactDate(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="tatl-age">Age</Label>
                <Input
                  id="tatl-age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="e.g. 9"
                />
              </div>

              <div>
                <Label htmlFor="tatl-gender">Gender</Label>
                <Input
                  id="tatl-gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  placeholder="Male / Female"
                />
              </div>

              <div>
                <Label htmlFor="tatl-location">Location</Label>
                <Input
                  id="tatl-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Community, City, State"
                  required
                />
              </div>

              <div>
                <Label htmlFor="tatl-video-url">Video URL (optional)</Label>
                <Input
                  id="tatl-video-url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="tatl-excerpt">Excerpt</Label>
                <Textarea
                  id="tatl-excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={4}
                  placeholder="Short preview shown on story cards"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="tatl-quote">Quote (optional)</Label>
                <Input
                  id="tatl-quote"
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  placeholder="A short quote from the story"
                />
              </div>

              <div>
                <Label htmlFor="tatl-cover-image">Cover Image</Label>
                <Input
                  id="tatl-cover-image"
                  type="file"
                  accept="image/*"
                  onChange={onCoverImageChange}
                  required
                />

                {coverPreview ? (
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="mt-3 max-h-72 w-full rounded-xl border object-cover"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                ) : null}
              </div>

              <div>
                <Label htmlFor="tatl-gallery-images">Gallery Images (optional)</Label>
                <Input
                  id="tatl-gallery-images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setGallery(Array.from(e.target.files || []))}
                />

                {gallery.length ? (
                  <p className="mt-2 text-sm text-stone-500">
                    {gallery.length} image{gallery.length === 1 ? "" : "s"} selected
                  </p>
                ) : null}
              </div>

              <label className="md:col-span-2 flex items-center gap-2 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
                Publish immediately
              </label>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-stone-200">
          <CardContent className="space-y-5 p-6">
            <div>
              <h3 className="text-lg font-bold text-stone-900">Story Body</h3>
              <p className="text-sm text-stone-500">
                Full rich editor with image upload, gallery blocks, embeds, links, and block settings.
              </p>
            </div>

            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
              <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                <div className="mb-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={() => singleImageInputRef.current?.click()}
                    className="rounded-xl bg-stone-900 hover:bg-stone-800"
                  >
                    Add Single Image
                  </Button>

                  <Button
                    type="button"
                    onClick={() => galleryInputRef.current?.click()}
                    className="rounded-xl bg-amber-700 hover:bg-amber-800"
                  >
                    Add Gallery Block
                  </Button>

                  <Button
                    type="button"
                    onClick={() => setShowEmbedBox(true)}
                    className="rounded-xl bg-blue-700 hover:bg-blue-800"
                  >
                    Embed Video/Social Link
                  </Button>
                </div>

                <input
                  ref={singleImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    insertSingleImage(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />

                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    openGalleryFiles(e.target.files);
                    e.target.value = "";
                  }}
                />

                {showEmbedBox ? (
                  <div className="mb-4 rounded-xl border border-stone-200 bg-white p-4">
                    <Label>Paste YouTube, Facebook, Instagram, TikTok or any link</Label>
                    <div className="mt-2 flex flex-col gap-2 md:flex-row">
                      <Input
                        value={embedUrl}
                        onChange={(e) => setEmbedUrl(e.target.value)}
                        placeholder="https://youtube.com/watch?v=..."
                      />

                      <Button
                        type="button"
                        onClick={insertEmbed}
                        className="bg-amber-700 hover:bg-amber-800"
                      >
                        Insert
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowEmbedBox(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}

                <div
                  className="blog-editor-wrapper rounded-xl bg-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectEditorBlock(e.target);
                  }}
                  onContextMenu={(e) => {
                    const target = e.target;
                    const block =
                      target?.closest?.("[data-post-block]") ||
                      (target?.tagName === "A" ? target : target?.closest?.("a"));

                    if (block) {
                      e.preventDefault();
                      e.stopPropagation();
                      selectEditorBlock(block);
                      setContextMenu({
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }
                  }}
                >
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={content}
                    onChange={(value) => setContent(value || "<p></p>")}
                    modules={modules}
                    formats={formats}
                    className="min-h-[520px]"
                  />
                </div>

                {contextMenu ? (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="fixed z-[9999] w-56 rounded-xl border border-stone-200 bg-white p-2 shadow-xl"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                  >
                    <button
                      type="button"
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-stone-100"
                      onClick={() => {
                        setContextMenu(null);
                        refreshSelectedBlock();
                      }}
                    >
                      Open Block Settings
                    </button>

                    <button
                      type="button"
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                      onClick={deleteSelectedBlock}
                    >
                      Delete Selected Block
                    </button>
                  </div>
                ) : null}
              </div>

              <aside className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-base font-bold text-stone-900">Block Settings</h3>

                  {selectedBlock ? (
                    <button
                      type="button"
                      onClick={() => {
                        clearSelectionOutline();
                        selectedElementRef.current = null;
                        setSelectedBlock(null);
                      }}
                      className="rounded-lg bg-stone-100 px-2 py-1 text-xs text-stone-700 hover:bg-stone-200"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                {!selectedBlock ? (
                  <div className="rounded-xl bg-stone-50 p-4 text-sm text-stone-600">
                    Click an image, gallery, embed, or link inside the story body to manage it here.
                  </div>
                ) : null}

                {selectedBlock?.type === "image" ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Image URL</Label>
                      <Input value={selectedBlock.src} readOnly className="text-xs" />
                    </div>

                    <div>
                      <Label>Caption</Label>
                      <Textarea
                        value={selectedBlock.caption}
                        rows={3}
                        placeholder="Optional caption"
                        onChange={(e) => updateImageSetting("caption", e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>Alt Text</Label>
                      <Input
                        value={selectedBlock.alt}
                        placeholder="Describe the image"
                        onChange={(e) => updateImageSetting("alt", e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>Image Width</Label>
                      <select
                        value={selectedBlock.width}
                        onChange={(e) => updateImageSetting("width", e.target.value)}
                        className="mt-1 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm"
                      >
                        <option value="full">Full Width</option>
                        <option value="75">75%</option>
                        <option value="50">50%</option>
                        <option value="33">33%</option>
                      </select>
                    </div>

                    <Button
                      type="button"
                      onClick={deleteSelectedBlock}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      Delete Image
                    </Button>
                  </div>
                ) : null}

                {selectedBlock?.type === "gallery" ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Gallery Layout</Label>
                      <select
                        value={selectedBlock.cols}
                        onChange={(e) => updateGalleryLayout(Number(e.target.value))}
                        className="mt-1 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm"
                      >
                        <option value={2}>Tiled 2 Columns</option>
                        <option value={4}>Tiled 4 Columns</option>
                        <option value={6}>Tiled 6 Columns</option>
                      </select>
                    </div>

                    <div className="space-y-3">
                      <Label>Gallery Images</Label>

                      {selectedBlock.items.map((item) => (
                        <div
                          key={`${item.src}-${item.index}`}
                          className="rounded-xl border border-stone-200 p-3"
                        >
                          <img
                            src={item.src}
                            alt=""
                            className="mb-2 h-28 w-full rounded-lg object-cover"
                          />

                          <Input
                            value={item.caption}
                            placeholder="Optional caption"
                            onChange={(e) =>
                              updateGalleryCaption(item.index, e.target.value)
                            }
                          />

                          <button
                            type="button"
                            onClick={() => deleteGalleryItem(item.index)}
                            className="mt-2 text-sm font-semibold text-red-600 hover:text-red-700"
                          >
                            Delete this image
                          </button>
                        </div>
                      ))}
                    </div>

                    <Button
                      type="button"
                      onClick={deleteSelectedBlock}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      Delete Gallery
                    </Button>
                  </div>
                ) : null}

                {selectedBlock?.type === "link" ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Link Text</Label>
                      <Input
                        value={selectedBlock.text}
                        onChange={(e) => updateLinkSetting("text", e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>Link URL</Label>
                      <Input
                        value={selectedBlock.href}
                        onChange={(e) => updateLinkSetting("href", e.target.value)}
                        placeholder="https://..."
                      />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-stone-700">
                      <input
                        type="checkbox"
                        checked={selectedBlock.target === "_blank"}
                        onChange={(e) =>
                          updateLinkSetting("target", e.target.checked)
                        }
                      />
                      Open in new tab
                    </label>

                    <Button
                      type="button"
                      onClick={deleteSelectedBlock}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      Remove Link
                    </Button>
                  </div>
                ) : null}

                {selectedBlock?.type === "embed" ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Embed URL</Label>
                      <Input
                        value={selectedBlock.url}
                        onChange={(e) =>
                          setSelectedBlock((s) => ({ ...s, url: e.target.value }))
                        }
                        onBlur={(e) => updateEmbedUrl(e.target.value)}
                        placeholder="https://youtube.com/..."
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={() => updateEmbedUrl(selectedBlock.url)}
                      className="w-full bg-blue-700 hover:bg-blue-800"
                    >
                      Update Embed
                    </Button>

                    <Button
                      type="button"
                      onClick={deleteSelectedBlock}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      Delete Embed
                    </Button>
                  </div>
                ) : null}
              </aside>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 md:grid-cols-2">
          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="mb-3 font-semibold text-stone-900">
                Highlights (one per line)
              </h3>
              <Textarea
                value={highlights}
                onChange={(e) => setHighlights(e.target.value)}
                className="min-h-[160px]"
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="mb-3 font-semibold text-stone-900">
                Needs Identified (one per line)
              </h3>
              <Textarea
                value={needs}
                onChange={(e) => setNeeds(e.target.value)}
                className="min-h-[160px]"
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="mb-3 font-semibold text-stone-900">
                Support Given (one per line)
              </h3>
              <Textarea
                value={supportGiven}
                onChange={(e) => setSupportGiven(e.target.value)}
                className="min-h-[160px]"
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
              <h3 className="mb-3 font-semibold text-stone-900">
                Outcomes (one per line)
              </h3>
              <Textarea
                value={outcomes}
                onChange={(e) => setOutcomes(e.target.value)}
                className="min-h-[160px]"
              />
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            disabled={!canSave || saving || uploadingImage}
            className="rounded-xl bg-amber-700 hover:bg-amber-800"
          >
            {saving ? "Saving..." : uploadingImage ? "Uploading..." : "Create Story"}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={() => nav("/admin/tatl")}
          >
            Cancel
          </Button>
        </div>
      </form>

      {galleryOpen ? (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-stone-900">
                Insert Image Gallery
              </h3>

              <button
                type="button"
                onClick={() => setGalleryOpen(false)}
                className="rounded-full bg-stone-100 px-3 py-1 text-sm"
              >
                Close
              </button>
            </div>

            <div className="mb-5">
              <Label>Gallery Layout</Label>
              <select
                value={galleryCols}
                onChange={(e) => setGalleryCols(Number(e.target.value))}
                className="mt-1 w-full rounded-xl border border-stone-300 px-3 py-2"
              >
                <option value={2}>Tiled 2 Columns</option>
                <option value={4}>Tiled 4 Columns</option>
                <option value={6}>Tiled 6 Columns</option>
              </select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {galleryImages.map((img, index) => (
                <div key={img.url} className="rounded-2xl border border-stone-200 p-3">
                  <img
                    src={img.url}
                    alt=""
                    className="mb-3 h-48 w-full rounded-xl object-cover"
                  />

                  <Input
                    value={img.caption}
                    placeholder="Optional caption"
                    onChange={(e) => {
                      const next = [...galleryImages];
                      next[index].caption = e.target.value;
                      setGalleryImages(next);
                    }}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setGalleryImages((items) => items.filter((_, i) => i !== index))
                    }
                    className="mt-2 text-sm font-semibold text-red-600 hover:text-red-700"
                  >
                    Remove from gallery
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setGalleryOpen(false)}
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={insertGallery}
                className="bg-amber-700 hover:bg-amber-800"
              >
                Insert Gallery
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .blog-editor-wrapper .ql-toolbar {
          border-top-left-radius: 0.75rem;
          border-top-right-radius: 0.75rem;
          border-color: #d6d3d1;
          background: #fafaf9;
        }

        .blog-editor-wrapper .ql-container {
          min-height: 520px;
          border-bottom-left-radius: 0.75rem;
          border-bottom-right-radius: 0.75rem;
          border-color: #d6d3d1;
          font-size: 17px;
          line-height: 1.8;
        }

        .blog-editor-wrapper .ql-editor {
          min-height: 520px;
          padding: 28px;
        }

        .blog-editor-wrapper .ql-editor h1 {
          font-size: 2.25rem;
          font-weight: 800;
          line-height: 1.15;
          margin: 1.2rem 0 0.8rem;
        }

        .blog-editor-wrapper .ql-editor h2 {
          font-size: 1.875rem;
          font-weight: 800;
          line-height: 1.2;
          margin: 1.1rem 0 0.7rem;
        }

        .blog-editor-wrapper .ql-editor h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.6rem;
        }

        .blog-editor-wrapper .ql-editor p {
          margin-bottom: 1rem;
        }

        .blog-editor-wrapper .ql-editor img {
          max-width: 100%;
          border-radius: 1rem;
        }

        .blog-editor-wrapper .ql-editor iframe {
          max-width: 100%;
        }

        .blog-editor-wrapper .ql-editor a {
          color: #b45309;
          text-decoration: underline;
        }

        .blog-editor-wrapper [data-post-block],
        .blog-editor-wrapper a {
          cursor: pointer;
        }

        .blog-editor-wrapper [data-post-block="image"] {
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }

        .blog-editor-wrapper [data-post-block="gallery"] figure {
          transition: opacity 0.2s ease;
        }
      `}</style>
    </AdminLayout>
  );
};

export default TatlStoryCreate;
